import React from "react";
import DataList from "components/datatable/DataList";
import PopupDialog from "components/dialog/PopupDialog";
import useAxios from "hooks/useAxios";
import useAxiosFunction from "hooks/useAxiosFunction";
import useMsgBox from "hooks/useMsgBox";
import { listItems, showEmpty, boolText, parseBoolean } from "features/kms/system/SystemPageUtils";
import { formatPersonName } from "features/kms/common/nameFormat";

const BLANK_MEDALI_FORM = {
  id_perolehan_medali: "",
  id_kejuaraan: "",
  id_cabor: "",
  id_nomor_pertandingan: "",
  id_jenis_medali: "",
  medali_bersama: false,
  id_kontingen: "",
  id_kontingen_bersama: "",
  atlet_peraih: "",
  atlet_peraih_bersama: "",
  id_kejuaraan_sdm_nomor: "",
  id_kejuaraan_sdm_nomor_bersama: "",
  id_tim: "",
  id_tim_bersama: "",
  catatan_skor_waktu: "",
};

function MedaliSelect({ label, value, options, valueId, labelId, onChange, disabled = false, error = "", colsize = 6 }) {
  return (
    <div className={`form-group col-md-${colsize}`}>
      <label>{label}</label>
      <select
        className={`form-control ${error ? "is-invalid" : ""}`}
        value={value || ""}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">-Pilih {label}-</option>
        {(options || []).map((item) => (
          <option key={item?.[valueId]} value={item?.[valueId]}>
            {item?.[labelId]}
          </option>
        ))}
      </select>
      {error ? <span className="error invalid-feedback">{error}</span> : null}
    </div>
  );
}

function MedaliTextInput({ label, value, onChange, colsize = 6, as = "input", error = "", disabled = false }) {
  const Component = as === "textarea" ? "textarea" : "input";
  return (
    <div className={`form-group col-md-${colsize}`}>
      <label>{label}</label>
      <Component
        className={`form-control ${error ? "is-invalid" : ""}`}
        value={value || ""}
        rows={as === "textarea" ? 3 : undefined}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? <span className="error invalid-feedback">{error}</span> : null}
    </div>
  );
}

function PeraihInput({ label, value, textValue, onChangeValue, onChangeText, options = [], optionValueId = "id", optionLabelId = "nama", error = "", disabled = false, useOptions = false }) {
  if (useOptions) {
    return (
      <div className="form-group col-md-6">
        <label>{label}</label>
        <select className={`form-control ${error ? "is-invalid" : ""}`} value={value || ""} disabled={disabled} onChange={(event) => onChangeValue(event.target.value)}>
          <option value="">-Pilih {label}-</option>
          {options.map((item) => (
            <option key={item?.[optionValueId]} value={item?.[optionValueId]}>
              {item?.[optionLabelId]}
            </option>
          ))}
        </select>
        {error ? <span className="error invalid-feedback">{error}</span> : null}
      </div>
    );
  }
  return (
    <div className="form-group col-md-6">
      <label>{label}</label>
      <input
        className={`form-control ${error ? "is-invalid" : ""}`}
        list={options.length < 1 ? "kejuaraan-admin-medali-atlet-list" : undefined}
        value={textValue || ""}
        disabled={disabled}
        onChange={(event) => onChangeText(event.target.value)}
      />
      {error ? <span className="error invalid-feedback">{error}</span> : null}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <tr>
      <th style={{ width: 190 }}>{label}</th>
      <td>{showEmpty(value, "-")}</td>
    </tr>
  );
}

function MedalDetailDialog({ show, item, onClose, onEdit }) {
  const isBersama = item?.medali_bersama === true || item?.medali_bersama === "true";
  const peraihLabel = String(item?.nomor_kategori_name || "").toLowerCase() === "perorangan" ? "Atlet Peraih" : "Tim Peraih";
  return (
    <PopupDialog
      title="Detail Pencatatan Medali"
      size="lg"
      show={show}
      closeHandle={onClose}
      closeLabel="Tutup"
      footerContent={
        onEdit ? (
          <button type="button" className="btn btn-primary" onClick={() => onEdit(item)}>
            Edit
          </button>
        ) : null
      }
    >
      <div className="table-responsive">
        <table className="table table-bordered table-striped mb-0">
          <tbody>
            <DetailRow label="Cabor" value={item?.cabor_name} />
            <DetailRow label="Nomor Pertandingan" value={item?.nomor_pertandingan_name} />
            <DetailRow label="Kategori" value={item?.nomor_kategori_name} />
            <DetailRow label="Medali" value={item?.medali_name} />
            <DetailRow label="Medali Bersama" value={boolText(isBersama, "Ya", "Tidak")} />
            <DetailRow label={isBersama ? "Kontingen 1" : "Kontingen"} value={item?.kontingen_1_name} />
            <DetailRow label={isBersama ? `${peraihLabel} Kontingen 1` : peraihLabel} value={item?.atlet_peraih} />
            {isBersama ? (
              <>
                <DetailRow label="Kontingen 2" value={item?.kontingen_2_name} />
                <DetailRow label={`${peraihLabel} Kontingen 2`} value={item?.atlet_peraih_bersama} />
              </>
            ) : null}
            <DetailRow label="Catatan Skor/Waktu" value={item?.catatan_skor_waktu} />
          </tbody>
        </table>
      </div>
    </PopupDialog>
  );
}

function MedaliForm({ show, initialValue, onClose, onSaved, resetKey = 0 }) {
  const [showMsg] = useMsgBox();
  const [, , loading, axiosFetch] = useAxiosFunction();
  const [kejuaraanResp] = useAxios({ method: "GET", url: "/references/kejuaraan-admin-medali-kejuaraan", auth: true });
  const [medaliResp] = useAxios({ method: "GET", url: "/references/medali", auth: true });
  const [form, setForm] = React.useState(BLANK_MEDALI_FORM);
  const [caborOptions, setCaborOptions] = React.useState([]);
  const [nomorOptions, setNomorOptions] = React.useState([]);
  const [kontingenOptions, setKontingenOptions] = React.useState([]);
  const [atletOptions, setAtletOptions] = React.useState([]);
  const [atletPeraihOptions, setAtletPeraihOptions] = React.useState([]);
  const [atletPeraihBersamaOptions, setAtletPeraihBersamaOptions] = React.useState([]);
  const [timPeraihOptions, setTimPeraihOptions] = React.useState([]);
  const [timPeraihBersamaOptions, setTimPeraihBersamaOptions] = React.useState([]);
  const [existingMedali, setExistingMedali] = React.useState([]);
  const [errors, setErrors] = React.useState({});
  const formRef = React.useRef(form);
  const isEditMode = Boolean(initialValue?.id_perolehan_medali);
  const isReadonlyExisting = !isEditMode && existingMedali.length > 0;
  const isPrimaryLocked = isEditMode;
  const isDetailLocked = isReadonlyExisting;
  const medaliOptions = listItems(medaliResp);
  const isKoniSendiriKontingen = React.useCallback(
    (idKontingen) => {
      const item = kontingenOptions.find((option) => String(option.id_kontingen) === String(idKontingen || ""));
      return parseBoolean(item?.is_koni_sendiri);
    },
    [kontingenOptions]
  );
  const selectedNomor = nomorOptions.find((item) => String(item?.id_nomor_pertandingan) === String(form.id_nomor_pertandingan || ""));
  const isPerorangan = String(selectedNomor?.kategori_kode || "PERORANGAN").toUpperCase() === "PERORANGAN";
  const primaryOwn = isKoniSendiriKontingen(form.id_kontingen);
  const bersamaOwn = isKoniSendiriKontingen(form.id_kontingen_bersama);

  React.useEffect(() => {
    formRef.current = form;
  }, [form]);

  React.useEffect(() => {
    if (!show) return;
    const kejuaraanItems = listItems(kejuaraanResp);
    setErrors({});
    setExistingMedali([]);
    setAtletPeraihOptions([]);
    setAtletPeraihBersamaOptions([]);
    setTimPeraihOptions([]);
    setTimPeraihBersamaOptions([]);
    setForm({
      ...BLANK_MEDALI_FORM,
      id_perolehan_medali: initialValue?.id_perolehan_medali || "",
      id_kejuaraan: initialValue?.id_kejuaraan || kejuaraanItems?.[0]?.id_kejuaraan || "",
      id_cabor: initialValue?.id_cabor || "",
      id_nomor_pertandingan: initialValue?.id_nomor_pertandingan || "",
      id_jenis_medali: initialValue?.id_jenis_medali || "",
      medali_bersama: initialValue?.medali_bersama === true || initialValue?.medali_bersama === "true",
      id_kontingen: initialValue?.id_kontingen || "",
      id_kontingen_bersama: initialValue?.id_kontingen_bersama || "",
      atlet_peraih: initialValue?.atlet_peraih || "",
      atlet_peraih_bersama: initialValue?.atlet_peraih_bersama || "",
      id_kejuaraan_sdm_nomor: initialValue?.id_kejuaraan_sdm_nomor || "",
      id_kejuaraan_sdm_nomor_bersama: initialValue?.id_kejuaraan_sdm_nomor_bersama || "",
      id_tim: initialValue?.id_tim || "",
      id_tim_bersama: initialValue?.id_tim_bersama || "",
      catatan_skor_waktu: initialValue?.catatan_skor_waktu || "",
    });
  }, [initialValue, resetKey, show]);

  React.useEffect(() => {
    if (!show || form.id_kejuaraan) return;
    const idKejuaraan = listItems(kejuaraanResp)?.[0]?.id_kejuaraan || "";
    if (idKejuaraan) {
      setForm((current) => (current.id_kejuaraan ? current : { ...current, id_kejuaraan: idKejuaraan }));
    }
  }, [form.id_kejuaraan, kejuaraanResp, show]);

  const updateForm = (key, value) => {
    setErrors((current) => ({ ...current, [key]: "" }));
    if (["id_kejuaraan", "id_cabor", "id_nomor_pertandingan", "id_jenis_medali"].includes(key)) {
      setExistingMedali([]);
      setAtletPeraihOptions([]);
      setAtletPeraihBersamaOptions([]);
      setTimPeraihOptions([]);
      setTimPeraihBersamaOptions([]);
    }
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === "id_kejuaraan") {
        next.id_perolehan_medali = "";
        next.id_cabor = "";
        next.id_nomor_pertandingan = "";
        next.id_jenis_medali = "";
        next.id_kontingen = "";
        next.id_kontingen_bersama = "";
        next.atlet_peraih = "";
        next.atlet_peraih_bersama = "";
        next.id_kejuaraan_sdm_nomor = "";
        next.id_kejuaraan_sdm_nomor_bersama = "";
        next.id_tim = "";
        next.id_tim_bersama = "";
      }
      if (key === "id_cabor") {
        next.id_perolehan_medali = "";
        next.id_nomor_pertandingan = "";
        next.id_jenis_medali = "";
        next.id_kontingen = "";
        next.id_kontingen_bersama = "";
        next.atlet_peraih = "";
        next.atlet_peraih_bersama = "";
        next.id_kejuaraan_sdm_nomor = "";
        next.id_kejuaraan_sdm_nomor_bersama = "";
        next.id_tim = "";
        next.id_tim_bersama = "";
      }
      if (key === "id_nomor_pertandingan") {
        next.id_perolehan_medali = "";
        next.id_jenis_medali = "";
        next.id_kontingen = "";
        next.id_kontingen_bersama = "";
        next.atlet_peraih = "";
        next.atlet_peraih_bersama = "";
        next.id_kejuaraan_sdm_nomor = "";
        next.id_kejuaraan_sdm_nomor_bersama = "";
        next.id_tim = "";
        next.id_tim_bersama = "";
      }
      if (key === "id_jenis_medali") {
        next.id_perolehan_medali = "";
        next.id_kontingen = "";
        next.id_kontingen_bersama = "";
        next.atlet_peraih = "";
        next.atlet_peraih_bersama = "";
        next.id_kejuaraan_sdm_nomor = "";
        next.id_kejuaraan_sdm_nomor_bersama = "";
        next.id_tim = "";
        next.id_tim_bersama = "";
        next.catatan_skor_waktu = "";
      }
      if (key === "id_kontingen") {
        setAtletPeraihOptions([]);
        setTimPeraihOptions([]);
        next.atlet_peraih = "";
        next.id_kejuaraan_sdm_nomor = "";
        next.id_tim = "";
      }
      if (key === "id_kontingen_bersama") {
        setAtletPeraihBersamaOptions([]);
        setTimPeraihBersamaOptions([]);
        next.atlet_peraih_bersama = "";
        next.id_kejuaraan_sdm_nomor_bersama = "";
        next.id_tim_bersama = "";
      }
      if (key === "medali_bersama" && value === false) {
        setAtletPeraihBersamaOptions([]);
        setTimPeraihBersamaOptions([]);
        next.id_kontingen_bersama = "";
        next.atlet_peraih_bersama = "";
        next.id_kejuaraan_sdm_nomor_bersama = "";
        next.id_tim_bersama = "";
      }
      return next;
    });
  };

  const populateMedaliForm = React.useCallback((item) => {
    if (!item) return;
    setForm((current) => ({
      ...current,
      id_perolehan_medali: item?.id_perolehan_medali || "",
      id_jenis_medali: item?.id_jenis_medali || "",
      medali_bersama: item?.medali_bersama === true || item?.medali_bersama === "true",
      id_kontingen: item?.id_kontingen || "",
      id_kontingen_bersama: item?.id_kontingen_bersama || "",
      atlet_peraih: item?.atlet_peraih || "",
      atlet_peraih_bersama: item?.atlet_peraih_bersama || "",
      id_kejuaraan_sdm_nomor: item?.id_kejuaraan_sdm_nomor || "",
      id_kejuaraan_sdm_nomor_bersama: item?.id_kejuaraan_sdm_nomor_bersama || "",
      id_tim: item?.id_tim || "",
      id_tim_bersama: item?.id_tim_bersama || "",
      catatan_skor_waktu: item?.catatan_skor_waktu || "",
    }));
  }, []);

  React.useEffect(() => {
    if (!show || !form.id_kejuaraan) {
      setCaborOptions([]);
      return;
    }
    axiosFetch({
      method: "GET",
      url: "/references/kejuaraan-admin-medali-cabor",
      auth: true,
      params: { id_kejuaraan: form.id_kejuaraan },
    }).then((resp) => setCaborOptions(listItems(resp)));
  }, [axiosFetch, form.id_kejuaraan, show]);

  React.useEffect(() => {
    if (!show || !form.id_kejuaraan || !form.id_cabor) {
      setNomorOptions([]);
      return;
    }
    axiosFetch({
      method: "GET",
      url: "/references/kejuaraan-admin-medali-nomor",
      auth: true,
      params: { id_kejuaraan: form.id_kejuaraan, id_cabor: form.id_cabor },
    }).then((resp) => setNomorOptions(listItems(resp)));
  }, [axiosFetch, form.id_cabor, form.id_kejuaraan, show]);

  React.useEffect(() => {
    if (!show || !form.id_kejuaraan) {
      setKontingenOptions([]);
      return;
    }
    axiosFetch({
      method: "GET",
      url: "/references/kejuaraan-admin-medali-kontingen",
      auth: true,
      params: { id_kejuaraan: form.id_kejuaraan },
    }).then((resp) => setKontingenOptions(listItems(resp)));
  }, [axiosFetch, form.id_kejuaraan, show]);

  React.useEffect(() => {
    if (!show || !form.id_kejuaraan || !form.id_cabor || !form.id_nomor_pertandingan) {
      setAtletOptions([]);
      return;
    }
    axiosFetch({
      method: "GET",
      url: "/references/kejuaraan-admin-medali-atlet",
      auth: true,
      params: {
        id_kejuaraan: form.id_kejuaraan,
        id_cabor: form.id_cabor,
        id_nomor_pertandingan: form.id_nomor_pertandingan,
      },
    }).then((resp) => setAtletOptions(listItems(resp)));
  }, [axiosFetch, form.id_cabor, form.id_kejuaraan, form.id_nomor_pertandingan, show]);

  React.useEffect(() => {
    if (isEditMode || !show || !form.id_kejuaraan || !form.id_cabor || !form.id_nomor_pertandingan || !form.id_jenis_medali) {
      setExistingMedali([]);
      return;
    }
    axiosFetch({
      method: "GET",
      url: "/kejuaraan-admin/perolehan-medali-by-nomor",
      auth: true,
      params: {
        id_cabor: form.id_cabor,
        id_nomor_pertandingan: form.id_nomor_pertandingan,
        id_jenis_medali: form.id_jenis_medali,
      },
    }).then((resp) => {
      const items = listItems(resp);
      setExistingMedali(items);
      if (items.length > 0) {
        populateMedaliForm(items[0]);
      }
    });
  }, [axiosFetch, form.id_cabor, form.id_jenis_medali, form.id_kejuaraan, form.id_nomor_pertandingan, isEditMode, populateMedaliForm, show]);

  const syncPeraihFromKontingenSendiri = React.useCallback(
    ({ idKontingen, textField, valueField, setTargetOptions, isTeam = false, kontingenField = "id_kontingen" }) => {
      if (isReadonlyExisting || !show || !form.id_kejuaraan || !form.id_cabor || !form.id_nomor_pertandingan || !idKontingen || !isKoniSendiriKontingen(idKontingen)) {
        setTargetOptions([]);
        return;
      }
      const requestContext = {
        idKejuaraan: String(form.id_kejuaraan || ""),
        idCabor: String(form.id_cabor || ""),
        idNomorPertandingan: String(form.id_nomor_pertandingan || ""),
        idKontingen: String(idKontingen || ""),
      };
      axiosFetch({
        method: "GET",
        url: isTeam ? "/references/kejuaraan-admin-medali-tim" : "/references/kejuaraan-admin-medali-atlet",
        auth: true,
        params: {
          id_kejuaraan: form.id_kejuaraan,
          id_cabor: form.id_cabor,
          id_nomor_pertandingan: form.id_nomor_pertandingan,
          id_kontingen: idKontingen,
        },
      }).then((resp) => {
        const latest = formRef.current || {};
        if (
          String(latest.id_kejuaraan || "") !== requestContext.idKejuaraan ||
          String(latest.id_cabor || "") !== requestContext.idCabor ||
          String(latest.id_nomor_pertandingan || "") !== requestContext.idNomorPertandingan ||
          String(latest[kontingenField] || "") !== requestContext.idKontingen
        ) {
          return;
        }
        const items = listItems(resp);
        setTargetOptions(items);
        const idKey = isTeam ? "id_tim" : "id_kejuaraan_sdm_nomor";
        const labelKey = isTeam ? "nama" : "nama";
        setForm((current) => {
          if (
            String(current.id_kejuaraan || "") !== requestContext.idKejuaraan ||
            String(current.id_cabor || "") !== requestContext.idCabor ||
            String(current.id_nomor_pertandingan || "") !== requestContext.idNomorPertandingan ||
            String(current[kontingenField] || "") !== requestContext.idKontingen
          ) {
            return current;
          }
          if (items.length < 1) {
            return { ...current, [valueField]: "", [textField]: "" };
          }
          if (items.length === 1) {
            return { ...current, [valueField]: items[0]?.[idKey] || "", [textField]: items[0]?.[labelKey] || "" };
          }
          if (items.length > 1 && !items.some((item) => String(item?.[idKey]) === String(current[valueField] || ""))) {
            return { ...current, [valueField]: "", [textField]: "" };
          }
          return current;
        });
        if (items.length === 1) {
          setErrors((current) => ({ ...current, [textField]: "" }));
        }
      });
    },
    [axiosFetch, form.id_cabor, form.id_kejuaraan, form.id_nomor_pertandingan, isKoniSendiriKontingen, isReadonlyExisting, show]
  );

  React.useEffect(() => {
    syncPeraihFromKontingenSendiri({
      idKontingen: form.id_kontingen,
      textField: "atlet_peraih",
      valueField: isPerorangan ? "id_kejuaraan_sdm_nomor" : "id_tim",
      setTargetOptions: isPerorangan ? setAtletPeraihOptions : setTimPeraihOptions,
      isTeam: !isPerorangan,
      kontingenField: "id_kontingen",
    });
  }, [form.id_kontingen, isPerorangan, syncPeraihFromKontingenSendiri]);

  React.useEffect(() => {
    if (!form.medali_bersama) return;
    syncPeraihFromKontingenSendiri({
      idKontingen: form.id_kontingen_bersama,
      textField: "atlet_peraih_bersama",
      valueField: isPerorangan ? "id_kejuaraan_sdm_nomor_bersama" : "id_tim_bersama",
      setTargetOptions: isPerorangan ? setAtletPeraihBersamaOptions : setTimPeraihBersamaOptions,
      isTeam: !isPerorangan,
      kontingenField: "id_kontingen_bersama",
    });
  }, [form.id_kontingen_bersama, form.medali_bersama, isPerorangan, syncPeraihFromKontingenSendiri]);

  const updatePeraihPilihan = ({ valueField, textField, value, options, optionValueId, optionLabelId }) => {
    const item = (options || []).find((option) => String(option?.[optionValueId]) === String(value || ""));
    setErrors((current) => ({ ...current, [textField]: "" }));
    setForm((current) => ({
      ...current,
      [valueField]: value,
      [textField]: item?.[optionLabelId] || "",
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    if (isReadonlyExisting) return;
    const nextErrors = {};
    if (!form.id_cabor) nextErrors.id_cabor = "Cabor wajib diisi.";
    if (!form.id_nomor_pertandingan) nextErrors.id_nomor_pertandingan = "Nomor Pertandingan wajib diisi.";
    if (!form.id_jenis_medali) nextErrors.id_jenis_medali = "Medali wajib diisi.";
    if (!form.id_kontingen) nextErrors.id_kontingen = "Kontingen wajib diisi.";
    if (primaryOwn) {
      const ownOptions = isPerorangan ? atletPeraihOptions : timPeraihOptions;
      if (ownOptions.length < 1) {
        nextErrors.atlet_peraih = isPerorangan
          ? "Belum ada atlet KONI sendiri pada nomor pertandingan ini."
          : "Belum ada tim KONI sendiri yang memiliki atlet pada nomor pertandingan ini.";
      } else if (isPerorangan && !form.id_kejuaraan_sdm_nomor) {
        nextErrors.atlet_peraih = "Atlet Peraih wajib diisi.";
      } else if (!isPerorangan && !form.id_tim) {
        nextErrors.atlet_peraih = "Tim Peraih wajib diisi.";
      }
    } else if (!String(form.atlet_peraih || "").trim()) {
      nextErrors.atlet_peraih = `${isPerorangan ? "Atlet" : "Tim"} Peraih wajib diisi.`;
    }
    if (form.medali_bersama) {
      if (!form.id_kontingen_bersama) nextErrors.id_kontingen_bersama = "Kontingen 2 wajib diisi.";
      if (bersamaOwn) {
        const ownOptions = isPerorangan ? atletPeraihBersamaOptions : timPeraihBersamaOptions;
        if (ownOptions.length < 1) {
          nextErrors.atlet_peraih_bersama = isPerorangan
            ? "Belum ada atlet KONI sendiri pada nomor pertandingan ini."
            : "Belum ada tim KONI sendiri yang memiliki atlet pada nomor pertandingan ini.";
        } else if (isPerorangan && !form.id_kejuaraan_sdm_nomor_bersama) {
          nextErrors.atlet_peraih_bersama = "Atlet Peraih Kontingen 2 wajib diisi.";
        } else if (!isPerorangan && !form.id_tim_bersama) {
          nextErrors.atlet_peraih_bersama = "Tim Peraih Kontingen 2 wajib diisi.";
        }
      } else if (!String(form.atlet_peraih_bersama || "").trim()) {
        nextErrors.atlet_peraih_bersama = `${isPerorangan ? "Atlet" : "Tim"} Peraih Kontingen 2 wajib diisi.`;
      }
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    const id = form.id_perolehan_medali || "";
    const resp = await axiosFetch({
      method: id ? "PUT" : "POST",
      url: id ? `/kejuaraan-admin/perolehan-medali/${id}` : "/kejuaraan-admin/perolehan-medali",
      auth: true,
      data: {
        ...form,
        id_jenis_medali: Number(form.id_jenis_medali || 0),
        medali_bersama: form.medali_bersama === true,
        id_kontingen: Number(form.id_kontingen || 0),
        id_kontingen_bersama: Number(form.id_kontingen_bersama || 0),
      },
    });
    if (resp?.data?.success === true) {
      await showMsg("Informasi", "Data berhasil disimpan.");
      onSaved?.();
      return;
    }
    await showMsg("Error", resp?.message || "Pencatatan Medali gagal disimpan");
  };

  return (
    <PopupDialog title="Pencatatan Medali" size="xl" show={show} closeHandle={onClose} allowClose={false}>
      <form onSubmit={save} noValidate>
        <div className="card mb-0">
          <div className="card-body">
            <div className="row">
              <MedaliSelect label="Cabor" value={form.id_cabor} options={caborOptions} valueId="id_cabor" labelId="nama" onChange={(value) => updateForm("id_cabor", value)} disabled={!form.id_kejuaraan || isPrimaryLocked} error={errors.id_cabor} />
              <MedaliSelect label="Nomor Pertandingan" value={form.id_nomor_pertandingan} options={nomorOptions} valueId="id_nomor_pertandingan" labelId="nama" onChange={(value) => updateForm("id_nomor_pertandingan", value)} disabled={!form.id_cabor || isPrimaryLocked} error={errors.id_nomor_pertandingan} />
              <>
                  <MedaliSelect label="Medali" value={form.id_jenis_medali} options={medaliOptions} valueId="id_jenis_medali" labelId="nama" onChange={(value) => updateForm("id_jenis_medali", value)} disabled={isPrimaryLocked} error={errors.id_jenis_medali} />
                  <div className="form-group col-md-6">
                    <label>Apakah Medali Bersama?</label>
                    <select className="form-control" value={form.medali_bersama ? "1" : "0"} disabled={isDetailLocked} onChange={(event) => updateForm("medali_bersama", event.target.value === "1")}>
                      <option value="0">Tidak</option>
                      <option value="1">Ya</option>
                    </select>
                  </div>
                  <MedaliSelect label={form.medali_bersama ? "Kontingen 1" : "Kontingen"} value={form.id_kontingen} options={kontingenOptions} valueId="id_kontingen" labelId="nama" onChange={(value) => updateForm("id_kontingen", value)} disabled={!form.id_kejuaraan || isDetailLocked} error={errors.id_kontingen} />
                  <PeraihInput
                    label={`${isPerorangan ? "Atlet" : "Tim"} Peraih${form.medali_bersama ? " Kontingen 1" : ""}`}
                    value={isPerorangan ? form.id_kejuaraan_sdm_nomor : form.id_tim}
                    textValue={form.atlet_peraih}
                    options={isPerorangan ? atletPeraihOptions : timPeraihOptions}
                    optionValueId={isPerorangan ? "id_kejuaraan_sdm_nomor" : "id_tim"}
                    optionLabelId="nama"
                    useOptions={primaryOwn}
                    disabled={isDetailLocked}
                    error={errors.atlet_peraih}
                    onChangeText={(value) => updateForm("atlet_peraih", value)}
                    onChangeValue={(value) => updatePeraihPilihan({
                      valueField: isPerorangan ? "id_kejuaraan_sdm_nomor" : "id_tim",
                      textField: "atlet_peraih",
                      value,
                      options: isPerorangan ? atletPeraihOptions : timPeraihOptions,
                      optionValueId: isPerorangan ? "id_kejuaraan_sdm_nomor" : "id_tim",
                      optionLabelId: "nama",
                    })}
                  />
                  {form.medali_bersama ? (
                    <>
                      <MedaliSelect label="Kontingen 2" value={form.id_kontingen_bersama} options={kontingenOptions.filter((item) => String(item.id_kontingen) !== String(form.id_kontingen))} valueId="id_kontingen" labelId="nama" onChange={(value) => updateForm("id_kontingen_bersama", value)} disabled={!form.id_kontingen || isDetailLocked} error={errors.id_kontingen_bersama} />
                      <PeraihInput
                        label={`${isPerorangan ? "Atlet" : "Tim"} Peraih Kontingen 2`}
                        value={isPerorangan ? form.id_kejuaraan_sdm_nomor_bersama : form.id_tim_bersama}
                        textValue={form.atlet_peraih_bersama}
                        options={isPerorangan ? atletPeraihBersamaOptions : timPeraihBersamaOptions}
                        optionValueId={isPerorangan ? "id_kejuaraan_sdm_nomor" : "id_tim"}
                        optionLabelId="nama"
                        useOptions={bersamaOwn}
                        disabled={isDetailLocked}
                        error={errors.atlet_peraih_bersama}
                        onChangeText={(value) => updateForm("atlet_peraih_bersama", value)}
                        onChangeValue={(value) => updatePeraihPilihan({
                          valueField: isPerorangan ? "id_kejuaraan_sdm_nomor_bersama" : "id_tim_bersama",
                          textField: "atlet_peraih_bersama",
                          value,
                          options: isPerorangan ? atletPeraihBersamaOptions : timPeraihBersamaOptions,
                          optionValueId: isPerorangan ? "id_kejuaraan_sdm_nomor" : "id_tim",
                          optionLabelId: "nama",
                        })}
                      />
                    </>
                  ) : null}
                  <datalist id="kejuaraan-admin-medali-atlet-list">
                    {atletOptions.map((item) => (
                      <option key={item.id_personal} value={item.nama} />
                    ))}
                  </datalist>
                  <MedaliTextInput label="Catatan Skor/Waktu" value={form.catatan_skor_waktu} onChange={(value) => updateForm("catatan_skor_waktu", value)} colsize={12} as="textarea" disabled={isDetailLocked} />
              </>
            </div>
          </div>
          <div className="card-footer">
            <div className="row">
              <div className="col-md-2">
                <button type="submit" className="btn btn-primary btn-block" disabled={loading || isReadonlyExisting}>
                  {loading ? "Menyimpan..." : "Simpan"}
                </button>
              </div>
              <div className="col-md-2">
                <button type="button" className="btn btn-default btn-block" onClick={onClose}>
                  Batal
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </PopupDialog>
  );
}

export function KejuaraanAdminLaporPerolehanMedaliPage() {
  const [caborResp] = useAxios({ method: "GET", url: "/references/kejuaraan-admin-medali-cabor", auth: true });
  const [showForm, setShowForm] = React.useState(false);
  const [formInitialValue, setFormInitialValue] = React.useState(null);
  const [formResetKey, setFormResetKey] = React.useState(0);
  const [detailItem, setDetailItem] = React.useState(null);
  const [reload, setReload] = React.useState(0);
  const [, , , axiosFetch] = useAxiosFunction();
  const caborOptions = listItems(caborResp);

  const openAdd = () => {
    setFormInitialValue(null);
    setFormResetKey((value) => value + 1);
    setShowForm(true);
  };

  const openMedaliDetail = async (id) => {
    if (!id) return;
    const resp = await axiosFetch({
      method: "GET",
      url: `/kejuaraan-admin/perolehan-medali/${id}`,
      auth: true,
    });
    if (resp?.data) {
      setDetailItem(resp.data);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setFormInitialValue(null);
  };

  const onSaved = () => {
    closeForm();
    setDetailItem(null);
    setReload((value) => value + 1);
  };

  const openEdit = (item) => {
    setFormInitialValue(item);
    setFormResetKey((value) => value + 1);
    setDetailItem(null);
    setShowForm(true);
  };

  const renderMedaliCell = (val, colname, item) => {
    const id = item?.[`${colname}_id`];
    if (!val) return "-";
    return (
      <button type="button" className="btn btn-link btn-sm p-0 text-left" style={{ whiteSpace: "pre-line" }} onClick={() => openMedaliDetail(id)}>
        {val}
      </button>
    );
  };

  const coltype = {
    id_kejuaraan_nomor: { type: "hidden", name: "id_kejuaraan_nomor" },
    id_cabor: { label: "Cabor", type: "dropdown", name: "id_cabor", options: caborOptions, valueId: "id_cabor", labelId: "nama", placeholder: "-Semua Cabor-" },
    cabor_name: { label: "Cabor", renderFunc: (val) => showEmpty(val), width: 140 },
    nomor_pertandingan_name: { label: "Nomor Pertandingan", renderFunc: (val) => showEmpty(val), width: 190 },
    nomor_kategori_name: { label: "Kategori", renderFunc: (val) => showEmpty(val), width: 145 },
    emas: { label: "Emas", renderFunc: renderMedaliCell, renderExportFunc: (val) => showEmpty(val, "-"), width: 170 },
    emas_bersama: { label: "Emas Bersama", renderFunc: (val) => showEmpty(val, "-"), renderExportFunc: (val) => showEmpty(val, "-") },
    emas_peraih: { label: "Peraih Emas", renderFunc: (val) => showEmpty(val, "-"), renderExportFunc: (val) => showEmpty(val, "-") },
    emas_catatan: { label: "Catatan Emas", renderFunc: (val) => showEmpty(val, "-"), renderExportFunc: (val) => showEmpty(val, "-") },
    perak: { label: "Perak", renderFunc: renderMedaliCell, renderExportFunc: (val) => showEmpty(val, "-"), width: 170 },
    perak_bersama: { label: "Perak Bersama", renderFunc: (val) => showEmpty(val, "-"), renderExportFunc: (val) => showEmpty(val, "-") },
    perak_peraih: { label: "Peraih Perak", renderFunc: (val) => showEmpty(val, "-"), renderExportFunc: (val) => showEmpty(val, "-") },
    perak_catatan: { label: "Catatan Perak", renderFunc: (val) => showEmpty(val, "-"), renderExportFunc: (val) => showEmpty(val, "-") },
    perunggu: { label: "Perunggu", renderFunc: renderMedaliCell, renderExportFunc: (val) => showEmpty(val, "-"), width: 170 },
    perunggu_bersama: { label: "Perunggu Bersama", renderFunc: (val) => showEmpty(val, "-"), renderExportFunc: (val) => showEmpty(val, "-") },
    perunggu_peraih: { label: "Peraih Perunggu", renderFunc: (val) => showEmpty(val, "-"), renderExportFunc: (val) => showEmpty(val, "-") },
    perunggu_catatan: { label: "Catatan Perunggu", renderFunc: (val) => showEmpty(val, "-"), renderExportFunc: (val) => showEmpty(val, "-") },
  };

  const columns = [
    "no",
    "cabor_name",
    "nomor_pertandingan_name",
    "nomor_kategori_name",
    "emas",
    "perak",
    "perunggu",
  ];
  const xlsColumns = [
    "no",
    "cabor_name",
    "nomor_pertandingan_name",
    "nomor_kategori_name",
    "emas",
    "emas_bersama",
    "emas_peraih",
    "emas_catatan",
    "perak",
    "perak_bersama",
    "perak_peraih",
    "perak_catatan",
    "perunggu",
    "perunggu_bersama",
    "perunggu_peraih",
    "perunggu_catatan",
  ];

  return (
    <>
      <DataList
        title="Pencatatan Medali"
        reload={reload}
        coltype={coltype}
        toolbar={[{ label: "Catat Medali", icon: "fa-plus", onClick: openAdd }]}
        dlist={{
          url: "/kejuaraan-admin/perolehan-medali",
          columns,
          filter: [{ dtype: "q", name: "q", colsize: 3 }, { dtype: "dropdown", name: "id_cabor", label: "Cabor", colsize: 3, options: caborOptions, valueId: "id_cabor", labelId: "nama", placeholder: "-Semua Cabor-" }],
          sort_by: ["cabor_name", "nomor_pertandingan_name", "nomor_kategori_name", "emas", "perak", "perunggu"],
        }}
        pdflist={{ title: "Pencatatan Medali", columns }}
        xlslist={{ title: "Pencatatan Medali", columns: xlsColumns }}
        pdfconfig={{ orientation: "landscape" }}
      />
      {showForm ? (
        <MedaliForm
          key={formResetKey}
          show={showForm}
          initialValue={formInitialValue}
          resetKey={formResetKey}
          onClose={closeForm}
          onSaved={onSaved}
        />
      ) : null}
      <MedalDetailDialog show={Boolean(detailItem)} item={detailItem} onClose={() => setDetailItem(null)} onEdit={openEdit} />
    </>
  );
}

export function KejuaraanAdminRekapMedaliKontingenPage() {
  const [detailKontingen, setDetailKontingen] = React.useState(null);
  const detailTitle = detailKontingen?.kontingen
    ? `Rekap Medali Per Cabor Kontingen ${detailKontingen.kontingen}`
    : "Rekap Medali Per Cabor";
  const detailColtype = {
    cabor_name: { label: "Cabor", renderFunc: (val) => showEmpty(val), width: 260 },
    emas: { label: "Emas", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 70, align: "center" },
    perak: { label: "Perak", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 70, align: "center" },
    perunggu: { label: "Perunggu", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 80, align: "center" },
    total: { label: "Total", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 70, align: "center" },
  };
  const detailColumns = ["no", "cabor_name", "emas", "perak", "perunggu", "total"];
  const coltype = {
    kode: { label: "Kode", renderFunc: (val) => showEmpty(val), width: 80, align: "center" },
    kontingen: { label: "Kontingen", renderFunc: (val) => showEmpty(val), width: 260 },
    emas: { label: "Emas", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 70, align: "center" },
    perak: { label: "Perak", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 70, align: "center" },
    perunggu: { label: "Perunggu", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 80, align: "center" },
    total: { label: "Total", renderFunc: (val) => showEmpty(val, "0"), renderExportFunc: (val) => showEmpty(val, "0"), width: 70, align: "center" },
    action: {
      label: "Aksi",
      width: 80,
      align: "center",
      renderFunc: (val, colname, item) => (
        <button type="button" className="btn btn-primary btn-xs" title="Detail" onClick={() => setDetailKontingen(item)}>
          <i className="fa fa-search" />
        </button>
      ),
    },
  };
  const columns = ["no", "kode", "kontingen", "emas", "perak", "perunggu", "total", "action"];
  const exportColumns = columns.filter((item) => item !== "action");

  return (
    <>
      <DataList
        title="Rekap Medali Kontingen"
        coltype={coltype}
        dlist={{
          url: "/kejuaraan-admin/rekap-medali-kontingen",
          columns,
          filter: [{ dtype: "q", name: "q", colsize: 3 }],
          sort_by: ["kode", "kontingen", "emas", "perak", "perunggu", "total"],
        }}
        pdflist={{ title: "Rekap Medali Kontingen", columns: exportColumns }}
        xlslist={{ title: "Rekap Medali Kontingen", columns: exportColumns }}
        pdfconfig={{ orientation: "portrait" }}
      />
      <PopupDialog
        title={`Detail Rekap Medali ${detailKontingen?.kontingen || ""}`}
        size="lg"
        show={Boolean(detailKontingen)}
        closeHandle={() => setDetailKontingen(null)}
        closeLabel="Tutup"
      >
        {detailKontingen ? (
          <DataList
            title={detailTitle}
            coltype={detailColtype}
            dlist={{
              url: `/kejuaraan-admin/rekap-medali-kontingen/${detailKontingen.id_kontingen}/cabor`,
              columns: detailColumns,
              filter: [{ dtype: "q", name: "q", colsize: 4 }],
              sort_by: ["cabor_name", "emas", "perak", "perunggu", "total"],
            }}
            pdflist={{ title: detailTitle, columns: detailColumns }}
            xlslist={{ title: detailTitle, columns: detailColumns }}
          />
        ) : null}
      </PopupDialog>
    </>
  );
}

export function KejuaraanAdminAtletPeraihMedaliPage() {
  const [caborResp] = useAxios({ method: "GET", url: "/references/kejuaraan-admin-medali-cabor", auth: true });
  const [medaliResp] = useAxios({ method: "GET", url: "/references/medali", auth: true });
  const caborOptions = listItems(caborResp);
  const medaliOptions = listItems(medaliResp);
  const coltype = {
    id_cabor: { label: "Cabor", type: "dropdown", name: "id_cabor", options: caborOptions, valueId: "id_cabor", labelId: "nama", placeholder: "-Semua Cabor-" },
    id_jenis_medali: { label: "Medali", type: "dropdown", name: "id_jenis_medali", options: medaliOptions, valueId: "id_jenis_medali", labelId: "nama", placeholder: "-Semua Medali-" },
    nama_atlet: {
      label: "Nama Atlet",
      renderFunc: (_val, _col, item) => showEmpty(formatPersonName(item, ["nama_atlet"])),
      renderExportFunc: (_val, _col, item) => showEmpty(formatPersonName(item, ["nama_atlet"])),
      width: 220,
    },
    tim_name: { label: "Tim", renderFunc: (val) => showEmpty(val, "-"), renderExportFunc: (val) => showEmpty(val, "-"), width: 160 },
    cabor_name: { label: "Cabor", renderFunc: (val) => showEmpty(val), width: 150 },
    nomor_pertandingan_name: { label: "Nomor Pertandingan", renderFunc: (val) => showEmpty(val), width: 220 },
    medali_name: { label: "Medali", renderFunc: (val) => showEmpty(val), width: 90, align: "center" },
  };
  const columns = ["no", "nama_atlet", "tim_name", "cabor_name", "nomor_pertandingan_name", "medali_name"];

  return (
    <DataList
      title="Atlet Peraih Medali"
      coltype={coltype}
      dlist={{
        url: "/kejuaraan-admin/atlet-peraih-medali",
        columns,
        filter: [
          { dtype: "q", name: "q", colsize: 3 },
          { dtype: "dropdown", name: "id_cabor", label: "Cabor", colsize: 3, options: caborOptions, valueId: "id_cabor", labelId: "nama", placeholder: "-Semua Cabor-" },
          { dtype: "dropdown", name: "id_jenis_medali", label: "Medali", colsize: 3, options: medaliOptions, valueId: "id_jenis_medali", labelId: "nama", placeholder: "-Semua Medali-" },
        ],
        sort_by: ["nama_atlet", "tim_name", "cabor_name", "nomor_pertandingan_name", "medali_name"],
      }}
      pdflist={{ title: "Atlet Peraih Medali", columns }}
      xlslist={{ title: "Atlet Peraih Medali", columns }}
      pdfconfig={{ orientation: "landscape" }}
    />
  );
}

export function KejuaraanAdminRekapMedaliAtletPage() {
  const [caborResp] = useAxios({ method: "GET", url: "/references/kejuaraan-admin-medali-cabor", auth: true });
  const caborOptions = listItems(caborResp);
  const renderMultiline = (val) => <span style={{ whiteSpace: "pre-line" }}>{showEmpty(val, "-")}</span>;
  const coltype = {
    id_cabor: { label: "Cabor", type: "dropdown", name: "id_cabor", options: caborOptions, valueId: "id_cabor", labelId: "nama", placeholder: "-Semua Cabor-" },
    cabor_name: { label: "Cabor", renderFunc: (val) => showEmpty(val), width: 150 },
    nama_atlet: {
      label: "Nama",
      renderFunc: (_val, _col, item) => showEmpty(formatPersonName(item, ["nama_atlet"])),
      renderExportFunc: (_val, _col, item) => showEmpty(formatPersonName(item, ["nama_atlet"])),
      width: 180,
    },
    emas: { label: "Emas", renderFunc: (val) => showEmpty(val, 0), width: 70, align: "center" },
    nomor_emas: { label: "Nomor Pertandingan", renderFunc: renderMultiline, renderExportFunc: (val) => showEmpty(val, "-"), width: 190 },
    perak: { label: "Perak", renderFunc: (val) => showEmpty(val, 0), width: 70, align: "center" },
    nomor_perak: { label: "Nomor Pertandingan", renderFunc: renderMultiline, renderExportFunc: (val) => showEmpty(val, "-"), width: 190 },
    perunggu: { label: "Perunggu", renderFunc: (val) => showEmpty(val, 0), width: 80, align: "center" },
    nomor_perunggu: { label: "Nomor Pertandingan", renderFunc: renderMultiline, renderExportFunc: (val) => showEmpty(val, "-"), width: 190 },
  };
  const columns = ["no", "cabor_name", "nama_atlet", "emas", "nomor_emas", "perak", "nomor_perak", "perunggu", "nomor_perunggu"];

  return (
    <DataList
      title="Rekap Medali Atlet"
      coltype={coltype}
      dlist={{
        url: "/kejuaraan-admin/rekap-medali-atlet",
        columns,
        filter: [
          { dtype: "q", name: "q", colsize: 4 },
          { dtype: "dropdown", name: "id_cabor", label: "Cabor", colsize: 4, options: caborOptions, valueId: "id_cabor", labelId: "nama", placeholder: "-Semua Cabor-" },
        ],
        sort_by: ["cabor_name", "nama_atlet", "emas", "perak", "perunggu"],
      }}
      pdflist={{ title: "Rekap Medali Atlet", columns }}
      xlslist={{ title: "Rekap Medali Atlet", columns }}
      pdfconfig={{ orientation: "landscape" }}
    />
  );
}
